<template>
  <v-app>
    <v-container fluid class="pa-6">
      <div class="card pa-5 mb-4">
        <h2 class="mb-1">นำเข้าและตรวจสอบข้อมูลบุคลากรจากไฟล์ Excel</h2>
        <p class="grey--text mb-0">
          รองรับไฟล์รายงานทั้ง 6 รูปแบบ (ข้าราชการพลเรือน / ครูและบุคลากรฯ / สถาบัน 38ก /
          สถานศึกษา 38ค / สถาบัน / สถาบัน 38ค) ระบบจะจับคู่คอลัมน์ให้อัตโนมัติ
        </p>
      </div>

      <!-- STEP 1: เลือกประเภทบุคคล + อัปโหลดไฟล์ -->
      <div class="card pa-5 mb-4">
        <h3 class="mb-3">1) เลือกประเภทบุคคลของไฟล์นี้ + อัปโหลดไฟล์</h3>
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              v-model="targetUserStatus"
              :items="userstatus"
              item-text="user_status_name"
              item-value="user_status_sub"
              label="ประเภทบุคคล (user_status) สำหรับข้อมูลทั้งไฟล์นี้"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="targetOrganizeType"
              :items="[
                { text: 'วิทยาลัย (มีรหัสสถานศึกษา)', value: 'college' },
                { text: 'สถาบัน / หน่วยศึกษานิเทศก์ / ส่วนกลาง', value: 'iven' }
              ]"
              label="ลักษณะสังกัด"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-file-input
              v-model="excelFile"
              accept=".xlsx"
              label="ไฟล์ .xlsx"
              prepend-icon="mdi-file-excel"
              outlined
              dense
              hide-details
              @change="onFileSelected"
            ></v-file-input>
          </v-col>
        </v-row>
      </div>

      <!-- STEP 2: preview + แก้ไข mapping สังกัดที่จับคู่ไม่ได้ -->
      <div class="card pa-5 mb-4" v-if="parsedRows.length">
        <div class="d-flex justify-space-between align-center mb-3">
          <h3 class="mb-0">
            2) ตรวจสอบข้อมูลที่อ่านได้ ({{ parsedRows.length }} แถว)
          </h3>
          <div>
            <v-chip small color="error" outlined class="mr-2" v-if="unresolvedAgencyCount">
              ยังไม่ได้จับคู่สังกัด {{ unresolvedAgencyCount }} แถว
            </v-chip>
            <v-btn
              color="primary"
              :loading="loadingCheck"
              :disabled="unresolvedAgencyCount > 0 || !targetUserStatus"
              @click="checkAgainstDatabase"
            >
              <v-icon left small>mdi-database-search</v-icon>
              ตรวจสอบข้อมูล
            </v-btn>
          </div>
        </div>

        <v-data-table
          :items="parsedRows"
          :headers="previewHeaders"
          dense
          class="clean-table"
          :items-per-page="10"
        >
          <template v-slot:item.college_code="{ item }">
            <div v-if="item.college_code">{{ item.college_code }}</div>
            <v-autocomplete
              v-else
              v-model="item.college_code"
              :items="users"
              item-text="agencys"
              item-value="user_code"
              placeholder="เลือกหน่วยงาน..."
              dense
              hide-details
              style="min-width: 220px;"
            ></v-autocomplete>
          </template>
        </v-data-table>
      </div>

      <!-- STEP 3: ผลตรวจสอบ + เลือกแถวที่จะนำเข้า -->
      <div class="card pa-5 mb-4" v-if="checkedRows.length">
        <div class="d-flex justify-space-between align-center mb-3">
          <h3 class="mb-0">3) ผลการตรวจสอบ — เลือกแถวที่จะนำเข้า</h3>
          <div>
            <v-chip small color="success" class="mr-1">เพิ่มใหม่ {{ summary.insert }}</v-chip>
            <v-chip small color="warning" class="mr-1">ปรับปรุง {{ summary.update }}</v-chip>
            <v-chip
              small
              color="grey"
              class="mr-1"
              style="cursor:pointer;"
              @click="showNoChange = !showNoChange"
            >
              ไม่เปลี่ยน {{ summary.nochange }}
              <v-icon x-small right>{{ showNoChange ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
            </v-chip>
            <v-chip small color="error">ผิดพลาด {{ summary.error }}</v-chip>
          </div>
        </div>
        <p class="text-caption grey--text mb-2" v-if="!showNoChange && summary.nochange">
          มี {{ summary.nochange }} รายการที่ข้อมูลตรงกับฐานข้อมูลทุกฟิลด์แล้ว จึงไม่แสดงในตาราง —
          กดที่ชิป "ไม่เปลี่ยน" ด้านบนเพื่อดู/บังคับเลือกนำเข้าซ้ำได้
        </p>

        <v-data-table
          v-model="selectedRows"
          :items="selectableCheckedRows"
          :headers="checkHeaders"
          show-select
          item-key="_rowId"
          dense
          class="clean-table"
          :items-per-page="15"
        >
          <template v-slot:item.action="{ item }">
            <v-chip small :color="actionColor(item.action)" dark>{{ actionLabel(item.action) }}</v-chip>
          </template>

          <template v-slot:item.diff="{ item }">
            <div v-if="item.diff && item.diff.length" class="text-caption">
              <div v-for="d in item.diff" :key="d.field">
                <b>{{ d.field }}</b>: {{ d.old }} → {{ d.new }}
              </div>
            </div>
            <span v-else-if="item.action === 'error'" class="error--text text-caption">
              {{ item.errorMessage }}
            </span>
            <span v-else class="grey--text text-caption">-</span>
          </template>
        </v-data-table>

        <div class="text-right mt-4">
          <v-btn
            color="success"
            large
            :loading="loadingCommit"
            :disabled="!selectedRows.length"
            @click="commitImport"
          >
            <v-icon left>mdi-content-save-check</v-icon>
            ยืนยันนำเข้าข้อมูล ({{ selectedRows.length }} แถว)
          </v-btn>
        </div>
      </div>

      <!-- STEP 4: สรุปผลการนำเข้า -->
      <div class="card pa-5" v-if="commitResult">
        <h3 class="mb-3">4) สรุปผลการนำเข้า</h3>
        <v-alert type="success" outlined dense>
          เพิ่มใหม่สำเร็จ {{ commitResult.inserted }} ราย / ปรับปรุงสำเร็จ {{ commitResult.updated }} ราย
        </v-alert>
        <v-alert type="error" outlined dense v-if="commitResult.failed && commitResult.failed.length">
          ล้มเหลว {{ commitResult.failed.length }} ราย:
          <ul>
            <li v-for="f in commitResult.failed" :key="f.id_card">
              {{ f.id_card }} — {{ f.reason }}
            </li>
          </ul>
        </v-alert>
      </div>
    </v-container>
  </v-app>
</template>

<script>
export default {
  name: "personnel_import_reconcile",

  data() {
    return {
      ApiKey: "HRvec2021",

      targetUserStatus: null,
      targetOrganizeType: "college",

      excelFile: null,
      parsedRows: [],

      userstatus: [],
      users: [],

      loadingCheck: false,
      loadingCommit: false,

      checkedRows: [],
      selectedRows: [],
      showNoChange: false, // ⭐ ใหม่: toggle แสดงแถว "ไม่เปลี่ยนแปลง" เพื่อตรวจสอบ/บังคับนำเข้าซ้ำ

      commitResult: null,

      previewHeaders: [
        { text: "เลขบัตร", value: "id_card" },
        { text: "สังกัด (ข้อความ)", value: "agency_name" },
        { text: "รหัสหน่วยงาน", value: "college_code" },
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "ตำแหน่ง", value: "position_name" },
        { text: "เลขที่ตำแหน่ง", value: "id_position" }
      ],

      checkHeaders: [
        { text: "การดำเนินการ", value: "action" },
        { text: "เลขบัตร", value: "id_card" },
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "สังกัด", value: "agency_name" },
        { text: "ตำแหน่ง", value: "position_name" },
        { text: "รายละเอียดที่เปลี่ยน", value: "diff", sortable: false }
      ]
    };
  },

  computed: {
    unresolvedAgencyCount() {
      return this.parsedRows.filter(r => !r.college_code).length;
    },

    selectableCheckedRows() {
      if (this.showNoChange) return this.checkedRows;
      return this.checkedRows.filter(r => r.action !== "nochange");
    },

    summary() {
      const s = { insert: 0, update: 0, nochange: 0, error: 0 };
      this.checkedRows.forEach(r => {
        if (s[r.action] !== undefined) s[r.action]++;
      });
      return s;
    }
  },

  async mounted() {
    await this.loadUserStatus();
    await this.loadUsers();
  },

  methods: {
    async loadUserStatus() {
      const result = await this.$http.post("user_status.php", { ApiKey: this.ApiKey });
      this.userstatus = result.data || [];
    },

    async loadUsers() {
      const result = await this.$http.post("user.php", { ApiKey: this.ApiKey, admin: "Ok" });
      this.users = result.data || [];
    },

    // ==================== อ่านไฟล์ + จับคู่คอลัมน์แบบ anchor-based ====================
    async onFileSelected(file) {
      this.parsedRows = [];
      this.checkedRows = [];
      this.selectedRows = [];
      this.commitResult = null;

      if (!file) return;

      const XLSX = await import("xlsx");
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });

      if (!rows.length) return;

      const header = rows[0].map(h => (h || "").toString().trim());
      const dataRows = rows.slice(1).filter(r => r.some(c => c !== ""));

      const map = this.buildColumnMap(header, dataRows);

      this.parsedRows = dataRows.map((r, i) => this.mapRowToPersonnel(r, map, i));
    },

    findIdx(header, name) {
      return header.findIndex(h => h === name);
    },

    looksNumericColumn(dataRows, colIdx) {
      if (colIdx < 0) return false;
      const sample = dataRows.slice(0, 5).map(r => r[colIdx]);
      return sample.every(v => v !== "" && !isNaN(Number(v)));
    },

    buildColumnMap(header, dataRows) {
      const idx = name => this.findIdx(header, name);
      const map = {};

      map.id_card = idx("เลขประชาชน");
      map.agency_name = idx("สังกัด");
      map.department = idx("หน่วยงาน"); // อาจไม่มี (-1)

      map.college_code = idx("รหัสสถานศึกษา");
      if (map.college_code === -1 && map.agency_name > 0) {
        const candidate = map.agency_name - 1;
        if (candidate > map.id_card && this.looksNumericColumn(dataRows, candidate)) {
          map.college_code = candidate;
        }
      }

      map.title_s = idx("คำนำหน้า");
      map.frist_name = idx("ชื่อ");
      map.last_name = idx("สกุล");
      map.id_position = idx("ตำแหน่งเลขที่");
      map.position_name = idx("ตำแหน่ง");
      map.type_positition = idx("ประเภท");

      const rangIdx = idx("ระดับ");
      map.rang_name = rangIdx !== -1 ? rangIdx : idx("วิทยะฐานะ");
      map.rang_level = idx("อันดับ คศ.");

      const gotIdx = idx("วันที่ได้รับ");
      map.rang_day = gotIdx;
      map.rang_month = gotIdx !== -1 ? gotIdx + 1 : -1;
      map.rang_year = gotIdx !== -1 ? gotIdx + 2 : -1;

      map.salary_s = idx("เงินเดือน");

      const birthIdx = idx("วันเกิด");
      map.brith_day = birthIdx;
      map.brith_month = birthIdx !== -1 ? birthIdx + 1 : -1;
      map.brith_year = birthIdx !== -1 ? birthIdx + 2 : -1;

      const appoinIdx = idx("วันบรรจุ");
      map.appoin_day = appoinIdx;
      map.appoin_month = appoinIdx !== -1 ? appoinIdx + 1 : -1;
      map.appoin_year = appoinIdx !== -1 ? appoinIdx + 2 : -1;

      map.retrire_year = idx("ปีเกษียณ");
      map.remark = idx("หมายเหตุ");
      map.ed_abb = idx("วุฒิ(สูงสุด)");
      map.ed_name = idx("วุฒิเต็ม");

      return map;
    },

    getVal(row, colIdx) {
      if (colIdx === undefined || colIdx === -1) return "";
      const v = row[colIdx];
      return v === undefined || v === null ? "" : String(v).trim();
    },

    mapRowToPersonnel(row, map, rowIndex) {
      const agencyName = this.getVal(row, map.agency_name);

      // พยายามจับคู่ college_code จากชื่อสังกัดกับรายชื่อหน่วยงานที่มีอยู่ในระบบ
      let collegeCode = this.getVal(row, map.college_code);
      if (!collegeCode && agencyName) {
        const match = this.users.find(
          u => u.agencys && u.agencys.toString().trim() === agencyName
        );
        if (match) collegeCode = match.user_code;
      }

      const idCard = this.getVal(row, map.id_card).padStart(13, "0");

      return {
        _rowId: `r${rowIndex}`,
        id_card: idCard,
        agency_name: agencyName,
        college_code: collegeCode || "",
        title_s: this.getVal(row, map.title_s),
        frist_name: this.getVal(row, map.frist_name),
        last_name: this.getVal(row, map.last_name),
        full_name:
          this.getVal(row, map.title_s) +
          this.getVal(row, map.frist_name) +
          " " +
          this.getVal(row, map.last_name),
        id_position: this.getVal(row, map.id_position),
        position_name: this.getVal(row, map.position_name),
        type_positition: this.getVal(row, map.type_positition),
        rang_name: this.getVal(row, map.rang_name),
        rang_level: this.getVal(row, map.rang_level),
        salary_s: this.getVal(row, map.salary_s),
        brith_day: this.getVal(row, map.brith_day),
        brith_month: this.getVal(row, map.brith_month),
        brith_year: this.getVal(row, map.brith_year),
        appoin_day: this.getVal(row, map.appoin_day),
        appoin_month: this.getVal(row, map.appoin_month),
        appoin_year: this.getVal(row, map.appoin_year),
        retrire_year: this.getVal(row, map.retrire_year),
        remark: this.getVal(row, map.remark),
        ed_abb: this.getVal(row, map.ed_abb),
        ed_name: this.getVal(row, map.ed_name)
      };
    },

    // ==================== ตรวจสอบกับฐานข้อมูลจริง (batch) ====================
    async checkAgainstDatabase() {
      this.loadingCheck = true;
      this.checkedRows = [];
      this.selectedRows = [];
      this.commitResult = null;

      try {
        const batchSize = 200;
        const results = [];

        for (let i = 0; i < this.parsedRows.length; i += batchSize) {
          const batch = this.parsedRows.slice(i, i + batchSize);
          const result = await this.$http.post("personnel_import.check.php", {
            ApiKey: this.ApiKey,
            user_status: this.targetUserStatus,
            organize_type: this.targetOrganizeType,
            rows: batch
          });
          results.push(...(result.data.rows || []));
        }

        this.checkedRows = results;
        // ค่าเริ่มต้น: ติ๊กเฉพาะแถวที่ insert/update, ไม่ติ๊ก error/nochange
        this.selectedRows = this.checkedRows.filter(
          r => r.action === "insert" || r.action === "update"
        );
      } catch (err) {
        console.error("check error:", err);
        Swal.fire({ icon: "error", title: "ตรวจสอบข้อมูลไม่สำเร็จ" });
      } finally {
        this.loadingCheck = false;
      }
    },

    actionLabel(action) {
      return (
        {
          insert: "เพิ่มใหม่",
          update: "ปรับปรุง",
          nochange: "ไม่เปลี่ยนแปลง",
          error: "ผิดพลาด"
        }[action] || action
      );
    },

    actionColor(action) {
      return (
        {
          insert: "success",
          update: "warning",
          nochange: "grey",
          error: "error"
        }[action] || "grey"
      );
    },

    // ==================== ยืนยันนำเข้าจริง ====================
    async commitImport() {
      const confirm = await Swal.fire({
        title: `ยืนยันนำเข้าข้อมูล ${this.selectedRows.length} รายการ?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });
      if (!confirm.isConfirmed) return;

      this.loadingCommit = true;
      try {
        const result = await this.$http.post("personnel_import.commit.php", {
          ApiKey: this.ApiKey,
          user_status: this.targetUserStatus,
          organize_type: this.targetOrganizeType,
          rows: this.selectedRows
        });

        if (result.data && result.data.status === false) {
          // backend ตอบกลับมาว่าล้มเหลว พร้อมเหตุผลจริง (ดู field "error")
          console.error("commit failed:", result.data);
          Swal.fire({
            icon: "error",
            title: "นำเข้าข้อมูลไม่สำเร็จ",
            text: result.data.error || result.data.message || "ไม่ทราบสาเหตุ"
          });
          return;
        }

        this.commitResult = result.data;
        Swal.fire({
          icon: "success",
          title: "นำเข้าข้อมูลเสร็จสิ้น",
          timer: 1500,
          showConfirmButton: false
        });
      } catch (err) {
        console.error("commit error:", err);
        const serverMsg =
          err.response && err.response.data
            ? err.response.data.error || err.response.data.message
            : null;
        Swal.fire({
          icon: "error",
          title: "นำเข้าข้อมูลไม่สำเร็จ",
          text: serverMsg || err.message
        });
      } finally {
        this.loadingCommit = false;
      }
    }
  }
};
</script>

<style scoped>
.card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
}
</style>